import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { api } from '@/lib/api';
import { CiscoLogo } from '@/components/CiscoLogo';
import cilLogo from '@/assets/CILLogo.png';

const CONTACT_EMAIL = 'innovation-labs@cisco.com';

interface AboutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ServiceVersion {
  gitSha: string;
  startedAt: string;
}

function formatStarted(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Which exact build of each service is running, and when that process last started — the main things you need when debugging "is this the version I think it is" or "did the redeploy actually restart it". */
function VersionRow() {
  const [controlUi, setControlUi] = useState<ServiceVersion | null>(null);
  const [sync, setSync] = useState<ServiceVersion | null>(null);

  useEffect(() => {
    fetch('/version')
      .then((r) => r.json())
      .then(setControlUi)
      .catch(() => setControlUi({ gitSha: 'unknown', startedAt: '' }));
    api
      .getSyncServerVersion()
      .then(setSync)
      .catch(() => setSync({ gitSha: 'unknown', startedAt: '' }));
  }, []);

  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border bg-muted/30 px-3 py-2 font-mono text-[11px]">
      <dt className="text-muted-foreground">Control UI</dt>
      <dd>{controlUi ? `${controlUi.gitSha}${controlUi.startedAt ? ` · started ${formatStarted(controlUi.startedAt)}` : ''}` : 'loading…'}</dd>
      <dt className="text-muted-foreground">Sync server</dt>
      <dd>{sync ? `${sync.gitSha}${sync.startedAt ? ` · started ${formatStarted(sync.startedAt)}` : ''}` : 'loading…'}</dd>
    </dl>
  );
}

export function AboutDialog({ open, onOpenChange }: AboutDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <div className="flex items-center gap-3">
          <CiscoLogo className="h-7 w-auto" />
          <div className="h-5 w-px bg-border" />
          <img src={cilLogo} alt="Cisco Innovation Labs" className="h-6 w-auto rounded-sm bg-white p-1" />
        </div>
        <DialogHeader>
          <DialogTitle>About cAspeR</DialogTitle>
          <DialogDescription>Cisco Innovation Labs &middot; marker-based AR demos</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            cAspeR overlays synchronised 3D content onto a physical space for live demos. An
            operator drives the room from this console, while everyone in the room points a
            phone at a printed marker and sees the same model change, at the same moment, on
            cue.
          </p>
          <p>
            It started as a Cisco Innovation Labs proof of concept and now runs booth demos,
            briefings, and lab tours.
          </p>
        </div>
        <VersionRow />
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <Mail className="h-4 w-4" />
          {CONTACT_EMAIL}
        </a>
      </DialogContent>
    </Dialog>
  );
}
